import classNames from 'classnames';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import Input from '../../components/Input/Input';
import { ChangeEventHandler, useEffect, useState } from 'react';
import { Loader } from '../../components/Loader/Loader';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header/Header';
import { useHistory } from 'react-router-dom';
import { confirmForgotPassword } from '../../core/auth';
import { useAppDispatch } from '../../hooks';
import { addNotification } from '../../core/store/notificationsSlice';
import {
  containsCapital,
  containsDigit,
  containsNonCapital,
  containsSpecialCharacter,
  isCodeValid,
  isPasswordLongEnough,
  isPasswordValid,
  noSpacesOnEnds,
} from '../../core/helpers';
import Button from '../../components/Button';

interface FormValues {
  confirmationCode: string;
  password: string;
}
const BLOCK_KEY = 'header';

const CreatePassword = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  let history = useHistory();

  const params = new URLSearchParams(history.location.search);

  const email = params.get('email');

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    validateOnBlur: true,
    initialValues: {
      confirmationCode: '',
      password: '',
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      if (!values.confirmationCode) {
        errors.confirmationCode = t('Required');
      }

      if (
        values.confirmationCode !== '' &&
        !isCodeValid(values.confirmationCode)
      ) {
        errors.confirmationCode = 'Verification code is not valid';
      }

      if (values.password === '' || !isPasswordValid(values.password)) {
        errors.password = JSON.stringify(
          PASSWORD_VALIDATION_PARAMETERS.reduce((acc: string[], item) => {
            if (
              passwordValidators[item] &&
              !passwordValidators[item](values.password)
            ) {
              acc.push(item);
            }
            return acc;
          }, [])
        );
      }

      return errors;
    },
    onSubmit: ({ confirmationCode, password }) => {
      if (error) setError('');
      setIsLoading(true);
      confirmForgotPassword({
        confirmationCode,
        password,
        email: email!.replace(' ', '+'),
      })
        .then(() => {
          setIsLoading(false);
          dispatch(
            addNotification({
              type: 'success',
              text: t('New password created'),
            })
          );
          history.push('/');
        })
        .catch((err) => {
          setIsLoading(false);
          if (err.name === 'CodeMismatchException') {
            setError('Verification code is not valid');
          } else if (err.name === 'ExpiredCodeException') {
            setError('Verification code is expired');
          } else {
            setError('Something went wrong, please try again');
          }
        });
    },
  });

  const onChangeField: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (error) setError('');
    formik.setFieldValue(
      e.target.name,
      e.target.value,
      formik.touched[e.target.name as keyof FormikTouched<FormValues>]
    );
  };

  useEffect(() => {
    if (!email) {
      history.push('/');
    }
  }, [email, history]);

  if (!email) return null;

  if (isLoading) return <Loader />;

  const passwordErrors = (formik.errors.password || '').slice();

  return (
    <>
      <Header blockKey={BLOCK_KEY} />
      <div
        className='Page Page--Login d-flex align-items-center justify-content-center'
        data-testid='login'
      >
        <div className='Login__container mb-lg-5 pb-lg-5'>
          <div className='Title--lg text-lg-center mb-4 fw-900'>
            {t('Create new password')}
          </div>
          <form onSubmit={formik.handleSubmit} noValidate className='w-100'>
            <div className={classNames('Login__content align-items-start')}>
              <Input
                label='Verification code from email'
                name='confirmationCode'
                id='confirmationCode'
                onChange={onChangeField}
                onBlur={formik.handleBlur}
                value={formik.values.confirmationCode}
                error={formik.errors.confirmationCode}
                touched={formik.touched.confirmationCode}
              />
              <Input
                label='Password'
                name='password'
                id='password'
                type='password'
                className='RegisterForm__password'
                onChange={onChangeField}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                error={formik.errors.password}
                touched={formik.touched.password}
              />
              <div className='RegisterForm__passwordRequirements row row-cols-auto gx-1'>
                {PASSWORD_VALIDATION_PARAMETERS.map((item: any, i) => (
                  <div className='col' key={item}>
                    <div
                      className={classNames('text-nowrap mb-1', {
                        'RegisterForm__badge rounded':
                          formik.touched.password &&
                          passwordErrors.includes(item),
                        'Text--green':
                          formik.touched.password &&
                          !passwordErrors.includes(item),
                      })}
                      data-testid={`password-validation-${item
                        .toLowerCase()
                        .replace(/\s/g, '_')}`}
                    >
                      {t(item)}
                      {i === 0 ? (
                        ':'
                      ) : i !== PASSWORD_VALIDATION_PARAMETERS.length - 1 ? (
                        <span className='text-dark'>,</span>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className='mt-3 pt-3'>
              <Button
                className='Login__btn w-100'
                type='submit'
                disabled={isLoading}
              >
                {t('Create')}
              </Button>
              {error && (
                <div className='Login__error text-center'>{t(error)}</div>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export const PASSWORD_VALIDATION_PARAMETERS = [
  'Min 8 characters',
  'number',
  'special character',
  'uppercase letter',
  'lowercase letter',
  'no whitespace from both ends',
];

export const passwordValidators: {
  [key: string]: (v: string) => boolean;
} = {
  'Min 8 characters': isPasswordLongEnough,
  number: containsDigit,
  'special character': containsSpecialCharacter,
  'uppercase letter': containsCapital,
  'lowercase letter': containsNonCapital,
  'no whitespace from both ends': noSpacesOnEnds,
};

export default CreatePassword;
